import React from 'react'
import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { tv } from 'tailwind-variants'
import { MentionMember, mentionMarkdownToDisplayMarkdown } from 'helpers/mentions'

interface CommentBodyProps {
  markdown: string
  members: MentionMember[]
}

const MENTION_HREF_PREFIX = 'mention:'

const commentBody = tv({
  slots: {
    // The comment popup (Menu) sets white-space: nowrap, which is inherited; reset
    // it so rendered comments wrap instead of overflowing the popup horizontally.
    container: 'whitespace-normal wrap-anywhere text-sm leading-normal text-body',
    // No Tailwind preflight in this app, so browser default <p> margins apply —
    // collapse them to a small bottom gap like the rest of the comment UI.
    paragraph: 'mt-0 mb-2 last:mb-0',
    link: 'text-blue underline',
    chip: 'inline rounded-sm px-1 py-px font-medium text-blue bg-really-light-blue select-none'
  },
  variants: {
    // Chips that resolved to a real member link to their profile; read as clickable.
    linked: {
      true: { chip: 'cursor-pointer no-underline hover:bg-light-blue' },
      false: { chip: 'cursor-default' }
    }
  }
})

const { container, paragraph, link, chip } = commentBody()

const CommentBody: React.FC<CommentBodyProps> = ({ markdown, members }) => {
  const memberById = new Map<string, MentionMember>()
  members.forEach((member: MentionMember): void => {
    memberById.set(member.publicId, member)
  })

  const anchor: Components['a'] = ({ href, children }) => {
    if (href && href.startsWith(MENTION_HREF_PREFIX)) {
      const uuid = href.slice(MENTION_HREF_PREFIX.length)
      // Prefer the member's CURRENT name; fall back to the snapshot baked into the
      // token (children, already @-prefixed by mentionMarkdownToDisplayMarkdown).
      const member = memberById.get(uuid)
      const label = member ? `@${member.name}` : children
      if (member && member.firebaseUserId) {
        return (
          <a
            data-testid="comment-mention-chip"
            className={chip({ linked: true })}
            href={`/profile/${member.firebaseUserId}/information`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {label}
          </a>
        )
      }
      return (
        <span data-testid="comment-mention-chip" className={chip({ linked: false })}>
          {label}
        </span>
      )
    }
    return (
      <a className={link()} href={href} rel="noopener noreferrer" target="_blank">
        {children}
      </a>
    )
  }

  const paragraphComponent: Components['p'] = ({ children }) => <p className={paragraph()}>{children}</p>

  // react-markdown's default URL sanitiser strips the `mention:` protocol (it only
  // allows http/https/etc.), so preserve it here while delegating other URLs to the
  // safe default transform.
  const urlTransform = (url: string): string => {
    if (url.startsWith(MENTION_HREF_PREFIX)) {
      return url
    }
    return defaultUrlTransform(url)
  }

  return (
    <div className={container()}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={urlTransform}
        components={{ a: anchor, p: paragraphComponent }}
      >
        {mentionMarkdownToDisplayMarkdown(markdown)}
      </ReactMarkdown>
    </div>
  )
}

export default CommentBody
